import React, { useEffect, useMemo, useState } from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { Controller, useFormContext } from 'react-hook-form'
import { Autocomplete, Chip } from '@mui/material'
import { IconButton, TextField } from '@common/material'
import { localizedStrings } from '@core/strings'
import { useGetEligibleParentsInfinite } from '@core/react-query/features/permits/hooks/useGetEligibleParentsInfinite'
import { Permit } from '@core/entities/permit/Permit'
import { PermitBase } from '@core/entities/permit/PermitBase'
import { PermitCard } from '@elements'
import ClearIcon from '@mui/icons-material/Clear'
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined'
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined'
import moment from 'moment'

type Props = {
  organisationId: number
  projectId: number
  childTemplateId: number
  initialParentPermitId?: number
  readOnly?: boolean
  selectedPermit?: Permit | PermitBase
  // When true, the selected template must be raised under a parent. If no
  // eligible parents exist, render a disabled picker with an error message
  // explaining what the user needs to do (see api PR #4163).
  requireParentPermit?: boolean
}

const FieldRow: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
}> = ({ icon, label, value }) => (
  <Flex alignItems='center' sx={{ gap: 3 }}>
    <Flex alignItems='center' sx={{ width: 120 }}>
      {icon}
      <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
        {label}
      </Text>
    </Flex>
    <Text sx={{ fontSize: 14, flex: 1 }}>{value}</Text>
  </Flex>
)

// Display for the selected parent permit. Rendered as an outlined fieldset
// with floating legend so it visually matches the disabled comboboxes
// (Project, Permit Template) above it on the same form. When `onClear` is
// provided the user can clear the selection and return to the picker.
const SelectedParentPermitDisplay: React.FC<{
  permit: Permit | PermitBase
  onClear?: () => void
}> = ({ permit, onClear }) => (
  <Box mt={2} mb={2}>
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.23)',
        borderRadius: 1,
        px: 2,
        pt: 2,
        pb: 1.5,
      }}
    >
      <Text
        component='span'
        sx={{
          position: 'absolute',
          top: -8,
          left: 9,
          px: 0.5,
          bgcolor: 'background.paper',
          fontSize: 12,
          color: 'text.secondary',
        }}
      >
        {`${localizedStrings.parentPermit} *`}
      </Text>
      {onClear && (
        <IconButton
          aria-label='clear-parent-permit'
          size='small'
          onClick={onClear}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <ClearIcon fontSize='small' />
        </IconButton>
      )}
      <Text fontSize={13} color='text.secondary'>
        {permit.template?.name ?? ''}
      </Text>
      <Text fontWeight={500} fontSize='1rem' sx={{ mt: 0.25 }}>
        {permit.summary}
      </Text>
      <Chip
        size='small'
        sx={{ mt: 0.5, mb: 1 }}
        color={PermitBase.getStatusChipColor(permit.status)}
        label={PermitBase.getStatusString(permit.status)}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {!!permit.startsOn && (
          <FieldRow
            icon={
              <EventAvailableOutlined
                sx={{ fontSize: 16, color: 'action.active' }}
              />
            }
            label={localizedStrings.startDate}
            value={moment(permit.startsOn).format('DD MMM YYYY [at] HH:mm')}
          />
        )}
        <FieldRow
          icon={
            <EventBusyOutlined sx={{ fontSize: 16, color: 'action.active' }} />
          }
          label={localizedStrings.endDate}
          value={
            permit.endsOn
              ? moment(permit.endsOn).format('DD MMM YYYY [at] HH:mm')
              : '-'
          }
        />
        {!!permit.location && (
          <FieldRow
            icon={
              <LocationOnOutlined
                sx={{ fontSize: 16, color: 'action.active' }}
              />
            }
            label={localizedStrings.location}
            value={permit.location}
          />
        )}
        {!!permit.shortUUID && (
          <FieldRow
            icon={
              <TextSnippetOutlined
                sx={{ fontSize: 16, color: 'action.active' }}
              />
            }
            label={localizedStrings.uniqueId}
            value={permit.shortUUID}
          />
        )}
        <FieldRow
          icon={
            <PersonOutlined sx={{ fontSize: 16, color: 'action.active' }} />
          }
          label={localizedStrings.permitHolder}
          value={permit.permitHolder?.getFullName() ?? ''}
        />
      </Box>
    </Box>
    <Text variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
      {localizedStrings.onlyOneParentCanBeSelected}
    </Text>
  </Box>
)

export const ParentPermitField: React.FC<Props> = ({
  organisationId,
  projectId,
  childTemplateId,
  initialParentPermitId,
  readOnly,
  selectedPermit,
  requireParentPermit,
}) => {
  const { setValue } = useFormContext()

  if (selectedPermit) {
    return (
      <SelectedParentPermitDisplay
        permit={selectedPermit}
        onClear={
          readOnly
            ? undefined
            : () => setValue('parentPermitId', null, { shouldDirty: true })
        }
      />
    )
  }

  return (
    <EditableParentPermitField
      organisationId={organisationId}
      projectId={projectId}
      childTemplateId={childTemplateId}
      initialParentPermitId={initialParentPermitId}
      requireParentPermit={requireParentPermit}
    />
  )
}

const EditableParentPermitField: React.FC<
  Pick<
    Props,
    | 'organisationId'
    | 'projectId'
    | 'childTemplateId'
    | 'initialParentPermitId'
    | 'requireParentPermit'
  >
> = ({
  organisationId,
  projectId,
  childTemplateId,
  initialParentPermitId,
  requireParentPermit,
}) => {
  const [listboxEl, setListboxEl] = useState<Element | null>(null)

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useGetEligibleParentsInfinite({
      organisationId,
      projectId,
      childTemplateId,
      size: 20,
    })

  const list = useMemo(() => data?.pages?.flat() ?? [], [data])

  const labelById = useMemo(() => {
    const map = new Map<number, string>()
    list.forEach((p) => map.set(p.id, `${p.name} — ${p.shortUUID}`))
    return map
  }, [list])

  useEffect(() => {
    if (!listboxEl || !hasNextPage || isFetchingNextPage) return
    if (listboxEl.scrollHeight <= listboxEl.clientHeight + 1) {
      fetchNextPage()
    }
  }, [listboxEl, list.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Optional template + no eligible parents → hide the picker entirely; the
  // user can submit without a parent. (Required-template case falls through
  // to the Autocomplete below, which renders disabled with an inline alert.)
  if (!initialParentPermitId && list.length === 0 && !requireParentPermit) {
    return null
  }

  // Required template + no eligible parents → render the picker disabled with
  // an inline alert so the user understands they need to raise a parent
  // permit first. The Controller's required rule still fires, so the staged
  // navigation's `handleSubmit` blocks Next regardless.
  const blockedNoEligible =
    !initialParentPermitId &&
    list.length === 0 &&
    !!requireParentPermit &&
    !isLoading

  const getHelperText = (errorMessage?: string): string => {
    if (blockedNoEligible) {
      return localizedStrings.parentPermitRequiredNoEligibleAlert
    }
    return errorMessage ?? localizedStrings.onlyOneParentCanBeSelected
  }

  return (
    <Box mt={2} mb={4}>
      <Controller
        name='parentPermitId'
        rules={{ required: localizedStrings.parentPermitRequired }}
        render={({ field: { value, onChange }, fieldState }) => (
          <Autocomplete
            disabled={blockedNoEligible}
            loading={isLoading || isFetchingNextPage}
            value={value ?? null}
            options={list.map(({ id }) => id)}
            onChange={(_, next: number | null) => onChange(next ?? undefined)}
            getOptionLabel={(o) => labelById.get(o) ?? ''}
            isOptionEqualToValue={(option, val) => option === val}
            ListboxProps={{
              ref: setListboxEl,
              sx: { maxHeight: 400 },
              onScroll: (event: React.SyntheticEvent) => {
                const target = event.currentTarget as HTMLElement
                const atBottom =
                  target.scrollTop + target.clientHeight >=
                  target.scrollHeight - 10
                if (atBottom && hasNextPage && !isFetchingNextPage) {
                  fetchNextPage()
                }
              },
            }}
            renderOption={(props, optionId) => {
              const permit = list.find((p) => p.id === optionId)
              if (!permit) return null
              const { key: _liKey, ...liProps } =
                props as React.HTMLAttributes<HTMLLIElement> & {
                  key?: React.Key
                }
              return (
                <li {...liProps} key={optionId} style={{ padding: 4 }}>
                  <PermitCard
                    templateName={permit.template?.name ?? ''}
                    summary={permit.summary}
                    status={permit.status}
                    startsOn={permit.startsOn}
                    endsOn={permit.endsOn}
                    location={permit.location}
                    shortUUID={permit.shortUUID}
                    permitHolder={permit.permitHolder}
                    sx={{ width: '100%', boxShadow: 'none' }}
                  />
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={localizedStrings.parentPermit}
                required
                error={!!fieldState.error || blockedNoEligible}
                helperText={getHelperText(fieldState.error?.message)}
                data-test='parent-permit-picker'
              />
            )}
          />
        )}
      />
    </Box>
  )
}
